/**
 * Buy Credits Modal
 * Displays credit packages and initiates Stripe Checkout
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { creditsService, CreditPackage } from '../services/CreditsService';
import { getApiBase } from '../config/api';
import { CreditsIcon } from './ui/CreditsIcon';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import './BuyCreditsModal.css';

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadPackages();
    }
  }, [isOpen]);

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const pkgs = await creditsService.getPackages();
      setPackages(pkgs);
    } catch (err: any) {
      console.error('Failed to load packages:', err);
      setError('Failed to load packages. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId: string) => {
    if (!user) {
      setError('Please sign in to purchase credits');
      return;
    }

    try {
      setPurchasing(packageId);
      setError(null);

      // Get current URL to return to after checkout
      const returnUrl = window.location.href;

      const response = await fetch(`${getApiBase()}/api/checkout/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': user.id,
          'x-user-email': user.email || '',
        },
        body: JSON.stringify({ packageId, returnUrl }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create checkout');
      }

      const { url } = await response.json();

      // Redirect to Stripe Checkout
      window.location.href = url;
      
      // Reset purchasing state after a delay in case the user cancels navigation
      // or the redirect fails. If navigation succeeds, the page unloads anyway.
      setTimeout(() => {
        if (purchasing) setPurchasing(null);
      }, 4000);
    } catch (err: any) {
      console.error('Purchase failed:', err);
      setError(err.message || 'Failed to start checkout. Please try again.');
      setPurchasing(null);
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatCredits = (num: number) => {
    return num.toLocaleString('en-US');
  };

  const getPerCreditPrice = (pkg: CreditPackage) => {
    const totalCredits = pkg.credits + pkg.bonusCredits;
    const pricePerCredit = (pkg.priceCents / 100) / totalCredits;
    if (pricePerCredit < 0.01) {
      return `${(pricePerCredit * 100).toFixed(1)}¢ per credit`;
    }
    if (pricePerCredit < 0.10) {
      return `${(pricePerCredit * 100).toFixed(0)}¢ per credit`;
    }
    return `$${pricePerCredit.toFixed(2)} per credit`;
  };

  // Iris-voiced descriptions per package (keyed by base credits)
  const getIrisDescription = (credits: number): string => {
    const map: Record<number, string> = {
      100:   "Dip in gently — enough to generate images and taste what's possible.",
      500:   "A creative spark to explore workflows and bring your first ideas to life.",
      1000:  "Real momentum — create freely, iterate fast, and see what ideas become.",
      2000:  "A generous creative runway to push ideas from concept to finished piece.",
      5000:  "Serious fuel for deep exploration — I'd pick this for committed creators.",
      10000: "My top pick — maximum creative freedom to build everything you dream up.",
    };
    return map[credits] ?? 'Unlock your creative potential with Knods AI generation.';
  };

  // Approximate what users can create — based on ~18 credits/image (mid-tier), ~160 credits/short video (5s Kling 2.6)
  const getUsageHint = (totalCredits: number): string => {
    const images = Math.floor(totalCredits / 18);
    const videos = Math.floor(totalCredits / 160);
    const imgStr = images >= 1000 ? `~${Math.round(images / 100) * 100}` : `~${images}`;
    const vidStr = `~${videos}`;
    return `${imgStr} images · ${vidStr} short videos`;
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalContent = (
    <div className="buy-credits-overlay" onClick={onClose}>
      <div className="buy-credits-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-left">
            <h2>
              <CreditsIcon size={24} />
              Unlock the Power of Knods
            </h2>
            <div className="modal-iris-byline">
              <img src="/icons/iris-butterfly.svg" alt="Iris" className="iris-byline-icon" />
              <span>Iris's picks for every creative level</span>
            </div>
          </div>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading packages...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <button onClick={loadPackages} className="retry-button">
                Try Again
              </button>
            </div>
          ) : (
            <>
              <p className="modal-description">
                Credits are used for AI generation. Choose a package that fits your needs.
              </p>
              
              <div className="packages-grid">
                {packages.map((pkg, index) => {
                  const totalCredits = pkg.credits + pkg.bonusCredits;
                  const isPopular = !pkg.isFeatured && index === 1;
                  return (
                    <div
                      key={pkg.id}
                      className={`package-card ${pkg.isFeatured ? 'featured' : ''} ${isPopular ? 'popular' : ''}`}
                    >
                      {pkg.isFeatured && (
                        <div className="featured-badge">
                          <StarIcon fontSize="small" />
                          Best Value
                        </div>
                      )}
                      {isPopular && (
                        <div className="popular-badge">Most Popular</div>
                      )}

                      {/* Credits — hero total with icon */}
                      <div className="credits-hero-row">
                        <CreditsIcon size={22} />
                        <span className="credits-hero-number">{formatCredits(totalCredits)}</span>
                      </div>

                      {/* Breakdown: base + bonus */}
                      {pkg.bonusCredits > 0 ? (
                        <div className="credits-breakdown">
                          {formatCredits(pkg.credits)} + <span className="credits-breakdown-bonus">{formatCredits(pkg.bonusCredits)} Bonus</span>
                        </div>
                      ) : (
                        <div className="credits-breakdown credits-breakdown-spacer" />
                      )}

                      {/* Price — secondary */}
                      <div className="package-price">{formatPrice(pkg.priceCents)}</div>

                      {/* Iris chat bubble — rolls in on hover */}
                      <div className="iris-quote-wrapper">
                        <img src="/icons/iris-butterfly.svg" alt="Iris" className="iris-quote-avatar" />
                        <div className="iris-quote-bubble">
                          {getIrisDescription(pkg.credits)}
                        </div>
                      </div>

                      <div className="usage-hint">{getUsageHint(totalCredits)}</div>
                      <div className="per-credit">{getPerCreditPrice(pkg)}</div>

                      <button
                        className={`purchase-button ${pkg.isFeatured ? 'featured' : isPopular ? 'popular' : ''}`}
                        onClick={() => handlePurchase(pkg.id)}
                        disabled={purchasing !== null}
                      >
                        {purchasing === pkg.id ? (
                          <>
                            <div className="button-spinner"></div>
                            Processing...
                          </>
                        ) : (
                          'Buy Now'
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
              
              <p className="payment-note">
                Secure payment powered by Stripe. You'll be redirected to complete your purchase.
              </p>
              <p className="credits-disclaimer">
                Credits are non-refundable and cannot be transferred or exchanged for cash. Valid for 2 years from purchase.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default BuyCreditsModal;
